import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../api.ts';
import { msg } from '../app/actions.ts';

const Brand = () => <div className="login-brand"><i className="ill livre login-art" aria-hidden="true" /><div className="wordmark-mark login-mark" role="img" aria-label="Sublime Nail Design" /><span>Nail Design</span></div>;

export function Login({ onDone }: { onDone: () => void }) {
  const [mode, setMode] = useState<'entrar' | 'esqueci' | 'enviado'>('entrar');
  const [email, setEmail] = useState(() => { try { return localStorage.getItem('sublime-email') ?? ''; } catch { return ''; } });
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [mailOn, setMailOn] = useState(true);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return setError('Digite o e-mail.');
    setBusy(true); setError('');
    try {
      if (mode === 'esqueci') {
        const r = await api.post<{ sent: boolean }>('/auth/forgot', { email: email.trim() });
        setMailOn(r.sent); setMode('enviado');
        return;
      }
      await api.post('/auth/login', { email: email.trim(), password });
      try { localStorage.setItem('sublime-email', email.trim()); } catch { /* sem armazenamento */ }
      onDone();
    } catch (err) { setError(msg(err)); } finally { setBusy(false); }
  }
  if (mode === 'enviado') {
    return (
      <div className="login view-enter"><Brand />
        <h1>Confira o seu e-mail</h1>
        <p className="login-sub">{mailOn ? `Se ${email.trim()} for de alguém da equipe, chegou um link para criar a senha nova. Ele vale por 1 hora. Olhe também a caixa de spam.` : 'O envio de e-mail ainda não está ligado no salão. Peça para a dona criar uma senha nova para você em Perfis de acesso.'}</p>
        <button className="cta login-cta" onClick={() => { setMode('entrar'); setPassword(''); }}>Voltar para entrar</button>
      </div>
    );
  }
  const forgot = mode === 'esqueci';
  return (
    <form className="login view-enter" onSubmit={submit} noValidate>
      <Brand />
      <h1>{forgot ? 'Esqueci minha senha' : 'Entrar'}</h1>
      <p className="login-sub">{forgot ? 'Digite o seu e-mail. Mandamos um link para você criar uma senha nova.' : 'Use o e-mail que a dona do salão cadastrou para você.'}</p>
      <label className="login-lbl" htmlFor="loginUser">E-mail</label>
      <input className="text-in" id="loginUser" autoComplete="username" inputMode="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="voce@sublime.com.br" />
      {!forgot && <>
        <label className="login-lbl" htmlFor="loginPass">Senha</label>
        <div className="login-pass">
          <input className="text-in" id="loginPass" type={show ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button type="button" onClick={() => setShow(!show)}>{show ? 'Esconder' : 'Mostrar'}</button>
        </div>
      </>}
      {error && <p className="login-err" role="alert">{error}</p>}
      <button className="cta login-cta" disabled={busy}>{busy ? (forgot ? 'Enviando…' : 'Entrando…') : forgot ? 'Mandar o link' : 'Entrar'}</button>
      <button type="button" className="link-back login-forgot" onClick={() => { setMode(forgot ? 'entrar' : 'esqueci'); setError(''); }}>{forgot ? 'Lembrei a senha' : 'Esqueci minha senha'}</button>
    </form>
  );
}

// página do link do e-mail: criar a senha nova (esqueci) ou a primeira (boas-vindas)
export function NovaSenha({ token }: { token: string }) {
  const q = useQuery({ queryKey: ['reset', token], queryFn: () => api.get<{ name: string; purpose: 'reset' | 'welcome' }>(`/auth/reset/${token}`), retry: false });
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  if (q.isLoading) return <div className="login"><Brand /><p className="login-sub">Abrindo…</p></div>;
  if (!q.data) {
    return (
      <div className="login view-enter"><Brand /><h1>Link expirado</h1>
        <p className="login-sub">Este link já foi usado ou passou do prazo. Peça outro em "Esqueci minha senha".</p>
        <a className="cta login-cta" href="/">Ir para entrar</a></div>
    );
  }
  if (done) {
    return (
      <div className="login view-enter"><Brand /><h1>Pronto, {q.data.name}!</h1>
        <p className="login-sub">A senha nova já vale. Entre com o seu e-mail e ela.</p>
        <a className="cta login-cta" href="/">Entrar no app</a></div>
    );
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (pw.length < 10) return setError('A senha precisa ter pelo menos 10 caracteres.');
    if (pw !== pw2) return setError('As duas senhas não são iguais.');
    setBusy(true); setError('');
    try { await api.post('/auth/reset', { token, password: pw }); setDone(true); } catch (err) { setError(msg(err)); } finally { setBusy(false); }
  }
  const welcome = q.data.purpose === 'welcome';
  return (
    <form className="login view-enter" onSubmit={submit} noValidate>
      <Brand />
      <h1>{welcome ? `Boas-vindas, ${q.data.name}!` : `Senha nova, ${q.data.name}`}</h1>
      <p className="login-sub">{welcome ? 'Crie a sua senha para entrar no app do salão.' : 'Escolha uma senha nova. As sessões abertas em outros aparelhos vão pedir para entrar de novo.'} Use pelo menos 10 caracteres; uma frase curta fica fácil de lembrar.</p>
      <label className="login-lbl" htmlFor="newPass">Senha nova</label>
      <div className="login-pass">
        <input className="text-in" id="newPass" type={show ? 'text' : 'password'} autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
        <button type="button" onClick={() => setShow(!show)}>{show ? 'Esconder' : 'Mostrar'}</button>
      </div>
      <label className="login-lbl" htmlFor="newPass2">Repita a senha</label>
      <input className="text-in" id="newPass2" type={show ? 'text' : 'password'} autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
      {error && <p className="login-err" role="alert">{error}</p>}
      <button className="cta login-cta" disabled={busy}>{busy ? 'Salvando…' : welcome ? 'Criar minha senha' : 'Salvar senha nova'}</button>
    </form>
  );
}
